import { ISavedColor } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import { Component } from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallMediumText from "../../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../../ui-kit/typography/Text/Text";
import AddColorPopover from "./add-color-popover";

enum SavedColorsListType {
  List = 1,
  Grid = 2
}

interface IColorListProps {
  colorList: ISavedColor[];
  newColorName: string;
  onNameChange: (name: string) => void;
  onAddColor: () => void;
  onDeleteColor: (id: string) => void;
  onColorSelect: (color: string) => void;
  currentHexColor: string;
  onEditColor?: (color: ISavedColor) => void;
  editingColor?: ISavedColor;
  addColorPopoverVisible?: boolean;
  onAddColorPopoverVisibleChange?: (visible: boolean) => void;
}

interface IColorListState {
  colorPickerListType: SavedColorsListType;
}

class ColorList extends Component<IColorListProps, IColorListState> {
  constructor(props: IColorListProps) {
    super(props);
    this.state = {
      colorPickerListType: SavedColorsListType.Grid
    };
  }

  render() {
    const {
      colorList,
      newColorName,
      onNameChange,
      onAddColor,
      onDeleteColor,
      onColorSelect,
      currentHexColor,
      onEditColor,
      addColorPopoverVisible,
      onAddColorPopoverVisibleChange
    } = this.props;
    const { colorPickerListType } = this.state;

    if (!colorList || colorList.length === 0) {
      return (
        <div className="no-saved-colors">
          <KuikaSmallMediumText>No saved colors found. Add now!</KuikaSmallMediumText>
          <AddColorPopover
            onAddColor={onAddColor}
            newColorName={newColorName}
            onNameChange={onNameChange}
            currentColor={currentHexColor}
            visible={addColorPopoverVisible}
            onVisibleChange={onAddColorPopoverVisibleChange}
          >
            <KuikaButton type="secondary" size="sm" isIcon>
              <KuikaSvg name="plus" />
            </KuikaButton>
          </AddColorPopover>
        </div>
      );
    }

    return (
      <div className="custom-color-presets">
        <div className="custom-color-header">
          <KuikaSmallMediumText>Saved colors</KuikaSmallMediumText>
          <div className="custom-color-header__icons">
            <KuikaButton
              size="sm"
              type="tertiary"
              color="neutral"
              isIcon
              active={colorPickerListType === SavedColorsListType.List}
              onClick={() => this.setState({ colorPickerListType: SavedColorsListType.List })}
            >
              <KuikaSvg name="list" type="styling-panel-icons" />
            </KuikaButton>
            <KuikaButton
              size="sm"
              type="tertiary"
              color="neutral"
              isIcon
              active={colorPickerListType === SavedColorsListType.Grid}
              onClick={() => this.setState({ colorPickerListType: SavedColorsListType.Grid })}
            >
              <KuikaSvg name="grip-horizontal" type="styling-panel-icons" />
            </KuikaButton>
          </div>
        </div>
        <div
          className="color-list"
          style={{
            flexDirection: colorPickerListType === SavedColorsListType.List ? "column" : "row",
            flexWrap: colorPickerListType === SavedColorsListType.List ? "nowrap" : "wrap",
            paddingLeft: colorPickerListType === SavedColorsListType.Grid ? 8 : 0
          }}
        >
          {colorList.map((item) => (
            <div
              key={item.id}
              className={
                colorPickerListType === SavedColorsListType.List
                  ? "color-item-wrapper color-item-wrapper__list"
                  : "color-item-wrapper"
              }
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.target && (e.target as HTMLElement).closest(".color-item__dropdown")) {
                  return;
                }
                onColorSelect(item.color);
              }}
            >
              <KuikaTooltip
                content={
                  item.color !== "transparent" ? `${KMainFunctions.convertRgbaToHex(item.color)}` : "Transparent"
                }
                position="top"
              >
                <div className="color-item" style={{ backgroundColor: item.color, position: "relative" }}>
                  {!item.isDefault && <div className="color-item__triangle" />}
                </div>
              </KuikaTooltip>
              {colorPickerListType === SavedColorsListType.List && (
                <div
                  className={classNames({
                    "color-item__labels": true,
                    "color-item__labels--editing": this.props.editingColor?.id === item.id
                  })}
                >
                  <KuikaSmallText className="color-name">{item.name}</KuikaSmallText>
                  {!item.isDefault && (
                    <div className="color-item__dropdown">
                      <KuikaDropdownMenu
                        options={[
                          {
                            label: <KuikaText>Edit</KuikaText>,
                            icon: <KuikaSvg width={16} height={16} name="edit" />,
                            onClick: () => onEditColor && onEditColor(item)
                          },
                          {
                            label: <KuikaText>Delete</KuikaText>,
                            icon: <KuikaSvg width={16} height={16} name="trash" />,
                            onClick: () => onDeleteColor(item.id),
                            danger: true
                          }
                        ]}
                        overlayClassName="color-item__dropdown-menu"
                        trigger="click"
                      >
                        {(open) => (
                          <div className={classNames("dropdown-button-wrapper", { "dropdown-open": open })}>
                            <KuikaButton isIcon type="tertiary" color="neutral" active={open}>
                              <KuikaSvg width={16} height={16} name="dots" />
                            </KuikaButton>
                          </div>
                        )}
                      </KuikaDropdownMenu>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }
}

export default ColorList;
export { SavedColorsListType };
